import firebase from 'firebase/compat/app'
import 'firebase/compat/database'

import {
  SECURITY_REASONS,
  createSecurityDecision,
  evaluateUserAccess,
  getSecurityPolicyConfig,
  hasConfiguredUserAllowlist
} from '@/utils/securityPolicy'

const DEFAULT_SUPERADMIN_EMAILS = Object.freeze(['herko@huntloc.com'])
const DEFAULT_BOOTSTRAP_ADMIN_EMAILS = Object.freeze([
  'herko@huntloc.com',
  'info@huntloc.lv',
  'info@traxby.com',
  'eldar.rihm@fob-solutions.com',
  'raul.siim@fob-solutions.com'
])
const ADMIN_DIRECTORY_CACHE_TTL_MS = 60 * 1000
const ADMIN_DIRECTORY_READ_TIMEOUT_MS = 4000

let cachedDirectoryRecords = null
let cachedDirectoryFetchedAt = 0
let pendingDirectoryPromise = null

function parseCsvList(value) {
  if (!value) return []

  return String(value)
    .split(',')
    .map((entry) => normalizeAdminEmail(entry))
    .filter(Boolean)
}

export function normalizeAdminEmail(value) {
  return String(value || '').trim().toLowerCase()
}

export function getSuperAdminEmails(env = process.env) {
  const configured = parseCsvList(env.VUE_APP_TIW_SUPERADMIN_EMAILS)
  return configured.length ? configured : [...DEFAULT_SUPERADMIN_EMAILS]
}

export function getBootstrapAdminEmails(env = process.env) {
  const configured = parseCsvList(
    env.VUE_APP_TIW_BOOTSTRAP_ADMIN_EMAILS || env.VUE_APP_TIW_BOOTSTRAP_ADMINS
  )
  return configured.length ? configured : [...DEFAULT_BOOTSTRAP_ADMIN_EMAILS]
}

export function isSuperAdminEmail(email, env = process.env) {
  const normalizedEmail = normalizeAdminEmail(email)
  return Boolean(normalizedEmail) && getSuperAdminEmails(env).includes(normalizedEmail)
}

export function canManageAdminDirectory(email, env = process.env) {
  return isSuperAdminEmail(email, env)
}

function getAdminDirectoryPath(env = process.env) {
  return env.VUE_APP_TIW_ADMIN_DIRECTORY_PATH || 'tiw/adminUsers'
}

function getAdminDirectoryRef(env = process.env) {
  return firebase.database().ref(getAdminDirectoryPath(env))
}

function normalizeAdminRecord(id, value = {}, env = process.env) {
  const email = normalizeAdminEmail(value.email)

  if (!email) {
    return null
  }

  return {
    id,
    email,
    role: isSuperAdminEmail(email, env) || value.role === 'superadmin' ? 'superadmin' : 'admin',
    enabled: value.enabled !== false,
    addedAt: value.addedAt || '',
    addedBy: normalizeAdminEmail(value.addedBy),
    updatedAt: value.updatedAt || '',
    updatedBy: normalizeAdminEmail(value.updatedBy),
    source: 'directory'
  }
}

function sortAdminRecords(records) {
  return [...records].sort((left, right) => {
    if (left.role !== right.role) {
      return left.role === 'superadmin' ? -1 : 1
    }

    return left.email.localeCompare(right.email)
  })
}

function createBootstrapAdminRecord(email, env = process.env) {
  return {
    id: `bootstrap:${email}`,
    email,
    role: isSuperAdminEmail(email, env) ? 'superadmin' : 'admin',
    enabled: true,
    addedAt: '',
    addedBy: '',
    updatedAt: '',
    updatedBy: '',
    source: 'bootstrap'
  }
}

function withTimeout(promise, timeoutMs, errorMessage) {
  let timeoutId = null

  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(errorMessage))
    }, timeoutMs)
  })

  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
  })
}

export function listBootstrapAdminRecords(env = process.env) {
  return sortAdminRecords(
    getBootstrapAdminEmails(env).map((email) => createBootstrapAdminRecord(email, env))
  )
}

export function invalidateAdminDirectoryCache() {
  cachedDirectoryRecords = null
  cachedDirectoryFetchedAt = 0
  pendingDirectoryPromise = null
}

export async function listDirectoryAdminRecords({ forceRefresh = false, env = process.env } = {}) {
  const isCacheValid =
    !forceRefresh &&
    cachedDirectoryRecords &&
    (Date.now() - cachedDirectoryFetchedAt) < ADMIN_DIRECTORY_CACHE_TTL_MS

  if (isCacheValid) {
    return [...cachedDirectoryRecords]
  }

  if (!pendingDirectoryPromise || forceRefresh) {
    pendingDirectoryPromise = withTimeout(
      getAdminDirectoryRef(env).once('value'),
      ADMIN_DIRECTORY_READ_TIMEOUT_MS,
      'Timed out while loading the TIW admin directory.'
    )
      .then((snapshot) => {
        const records = []

        snapshot.forEach((childSnapshot) => {
          const normalizedRecord = normalizeAdminRecord(childSnapshot.key, childSnapshot.val(), env)
          if (normalizedRecord) {
            records.push(normalizedRecord)
          }
        })

        cachedDirectoryRecords = sortAdminRecords(records)
        cachedDirectoryFetchedAt = Date.now()
        return [...cachedDirectoryRecords]
      })
      .finally(() => {
        pendingDirectoryPromise = null
      })
  }

  return pendingDirectoryPromise
}

export async function listEffectiveAdminRecords({ forceRefresh = false, env = process.env } = {}) {
  const recordsByEmail = new Map(
    listBootstrapAdminRecords(env).map((record) => [record.email, record])
  )

  try {
    const directoryRecords = await listDirectoryAdminRecords({ forceRefresh, env })
    directoryRecords.forEach((record) => {
      const existingRecord = recordsByEmail.get(record.email)
      recordsByEmail.set(record.email, {
        ...(existingRecord || {}),
        ...record,
        role: isSuperAdminEmail(record.email, env) || record.role === 'superadmin' ? 'superadmin' : 'admin',
        source: existingRecord ? 'bootstrap + directory' : 'directory'
      })
    })
  } catch (error) {
    return sortAdminRecords(Array.from(recordsByEmail.values()))
  }

  return sortAdminRecords(Array.from(recordsByEmail.values()))
}

export async function evaluateTiwAdminAccess(email, env = process.env) {
  const normalizedEmail = normalizeAdminEmail(email)

  if (!normalizedEmail) {
    return createSecurityDecision(false, SECURITY_REASONS.EMAIL_NOT_ALLOWED)
  }

  const securityConfig = getSecurityPolicyConfig(env)
  if (hasConfiguredUserAllowlist(securityConfig)) {
    const staticDecision = evaluateUserAccess(normalizedEmail, securityConfig)
    if (staticDecision.allowed) {
      return createSecurityDecision(true, '', {
        email: normalizedEmail,
        role: isSuperAdminEmail(normalizedEmail, env) ? 'superadmin' : 'admin',
        source: 'static-allowlist'
      })
    }
  }

  if (getBootstrapAdminEmails(env).includes(normalizedEmail)) {
    return createSecurityDecision(true, '', {
      email: normalizedEmail,
      role: isSuperAdminEmail(normalizedEmail, env) ? 'superadmin' : 'admin',
      source: 'bootstrap'
    })
  }

  try {
    const directoryRecords = await listDirectoryAdminRecords({ env })
    const matchingRecord = directoryRecords.find(
      (record) => record.email === normalizedEmail && record.enabled !== false
    )

    if (matchingRecord) {
      return createSecurityDecision(true, '', {
        email: normalizedEmail,
        role: matchingRecord.role,
        source: matchingRecord.source,
        record: matchingRecord
      })
    }
  } catch (error) {
    return createSecurityDecision(false, SECURITY_REASONS.ADMIN_DIRECTORY_UNAVAILABLE, {
      email: normalizedEmail,
      error
    })
  }

  return createSecurityDecision(false, SECURITY_REASONS.EMAIL_NOT_ALLOWED, {
    email: normalizedEmail
  })
}

export async function saveAdminDirectoryUser({
  email,
  role = 'admin',
  actorEmail,
  env = process.env
}) {
  const normalizedEmail = normalizeAdminEmail(email)
  const normalizedActorEmail = normalizeAdminEmail(actorEmail)

  if (!canManageAdminDirectory(normalizedActorEmail, env)) {
    throw new Error('Only a TIW superadmin can manage the admin directory.')
  }

  if (!normalizedEmail) {
    throw new Error('Admin email is required.')
  }

  const directoryRecords = await listDirectoryAdminRecords({ env })
  const matchingRecord = directoryRecords.find((record) => record.email === normalizedEmail)
  const timestamp = new Date().toISOString()
  const payload = {
    email: normalizedEmail,
    role: role === 'superadmin' ? 'superadmin' : 'admin',
    enabled: true,
    updatedAt: timestamp,
    updatedBy: normalizedActorEmail
  }

  if (matchingRecord) {
    await getAdminDirectoryRef(env).child(matchingRecord.id).update(payload)
  } else {
    await getAdminDirectoryRef(env).push({
      ...payload,
      addedAt: timestamp,
      addedBy: normalizedActorEmail
    })
  }

  invalidateAdminDirectoryCache()
}

export async function setAdminDirectoryUserEnabled({
  id,
  enabled,
  actorEmail,
  env = process.env
}) {
  const normalizedActorEmail = normalizeAdminEmail(actorEmail)

  if (!canManageAdminDirectory(normalizedActorEmail, env)) {
    throw new Error('Only a TIW superadmin can manage the admin directory.')
  }

  if (!id || String(id).startsWith('bootstrap:')) {
    throw new Error('Bootstrap admin entries cannot be changed from the TIW directory.')
  }

  await getAdminDirectoryRef(env).child(id).update({
    enabled: Boolean(enabled),
    updatedAt: new Date().toISOString(),
    updatedBy: normalizedActorEmail
  })

  invalidateAdminDirectoryCache()
}
